<template>
  <main class="tr grow">
    <!-- Dark split hero (editorial idiom): instant answer to what the page is -->
    <header class="tr-hero">
      <div class="tr-hero__shell">
        <div class="tr-hero__copy">
          <h1>How Workspace369 handles your data and keeps your business running.</h1>
          <p class="tr-lede">
            What this page covers: the security practices and payment handling built into the
            product, the privacy, terms, and cookie policies that govern your data, the public
            record of how the product changes, and direct answers to the questions buyers
            actually ask.
          </p>
          <p class="tr-hero__facts">
            <span>Workspace369 LLC — company and product entity</span>
            <span>Five public plans, $29–$699/mo</span>
            <span>Enterprise plan with 50 seats and audit trails</span>
            <span>Stripe payment processing built in</span>
          </p>
        </div>

        <figure class="tr-hero__visual">
          <img
            :src="heroArt.src"
            :alt="heroArt.alt"
            width="800"
            height="600"
            loading="eager"
            fetchpriority="high"
          />
        </figure>
      </div>
    </header>

    <div class="tr-col">
      <!-- Security practices + operations -->
      <section class="tr-section" aria-labelledby="tr-security-title">
        <div class="tr-head">
          <h2 id="tr-security-title">Security practices you can check before you buy.</h2>
        </div>
        <div class="tr-split">
          <ul class="tr-rows">
            <li v-for="item in securityPractices" :key="item.title" class="tr-row">
              <p class="tr-row__label">{{ item.label }}</p>
              <div>
                <h3>{{ item.title }}</h3>
                <p>{{ item.copy }}</p>
              </div>
            </li>
          </ul>
          <figure class="tr-figure">
            <img :src="portalArt.src" :alt="portalArt.alt" loading="lazy" width="800" height="600" />
            <figcaption>{{ portalArt.alt }}.</figcaption>
          </figure>
        </div>
      </section>

      <!-- Account and data security (each row traced to product code) -->
      <section class="tr-section" aria-labelledby="tr-account-title">
        <div class="tr-head">
          <h2 id="tr-account-title">Account and data security.</h2>
        </div>
        <ul class="tr-rows">
          <li v-for="item in accountSecurity" :key="item.title" class="tr-row">
            <p class="tr-row__label">{{ item.label }}</p>
            <div>
              <h3>{{ item.title }}</h3>
              <p>{{ item.copy }}</p>
            </div>
          </li>
        </ul>
      </section>

      <!-- Data & privacy -->
      <section class="tr-section" aria-labelledby="tr-privacy-title">
        <div class="tr-head">
          <h2 id="tr-privacy-title">The policies that govern your data are public and plain.</h2>
        </div>
        <ul class="tr-rows">
          <li v-for="item in legalLinks" :key="item.path" class="tr-row tr-row--link">
            <NuxtLink :to="localePath(item.path)" class="tr-row__title">{{ item.title }}</NuxtLink>
            <p>{{ item.copy }}</p>
          </li>
        </ul>
      </section>

      <!-- Reliability & transparency -->
      <section class="tr-section" aria-labelledby="tr-reliability-title">
        <div class="tr-head">
          <h2 id="tr-reliability-title">How the product changes is on the record.</h2>
        </div>
        <div class="tr-split">
          <ul class="tr-rows">
            <li v-for="item in reliabilityLinks" :key="item.path" class="tr-row tr-row--link">
              <NuxtLink :to="localePath(item.path)" class="tr-row__title">{{ item.title }}</NuxtLink>
              <p>{{ item.copy }}</p>
            </li>
          </ul>
          <figure class="tr-figure">
            <img
              :src="reportingArt.src"
              :alt="reportingArt.alt"
              loading="lazy"
              width="800"
              height="600"
            />
            <figcaption>{{ reportingArt.alt }}.</figcaption>
          </figure>
        </div>
      </section>

      <!-- Honest fit -->
      <section class="tr-section" aria-labelledby="tr-fit-title">
        <div class="tr-head">
          <h2 id="tr-fit-title">Honest about where Workspace369 fits — and where it does not.</h2>
        </div>
        <div class="tr-fit-grid">
          <article class="tr-fit-card">
            <h3>
              The work, client, message, invoice, and report need to stay attached.
            </h3>
            <ul>
              <li v-for="item in bestFit" :key="item">{{ item }}</li>
            </ul>
          </article>
          <article class="tr-fit-card">
            <h3>Some teams should keep a specialist platform.</h3>
            <ul>
              <li v-for="item in betterElsewhere" :key="item">{{ item }}</li>
            </ul>
          </article>
        </div>
      </section>

      <!-- Direct answers: the gl-faq treatment, editorial tokens -->
      <section class="tr-section tr-faq" aria-labelledby="tr-faq-title">
        <div class="tr-head">
          <h2 id="tr-faq-title">Direct answers build more trust than vague badges.</h2>
        </div>
        <div class="tr-faq__list">
          <details v-for="item in faq" :key="item.q" class="tr-faq__item">
            <summary>
              <h3>{{ item.q }}</h3>
              <svg viewBox="0 0 16 16" aria-hidden="true"><path d="m4 6 4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </summary>
            <p>{{ item.a }}</p>
          </details>
        </div>
      </section>

      <!-- Typographic close (editorial idiom) -->
      <section class="tr-next" aria-label="Try Workspace369" data-sticky-cta-hide>
        <h2>Start a workspace and check this page against the real product.</h2>
        <p class="tr-next__copy">
          Every claim here is checkable: the product is a 14-day free trial away, the pricing is
          public, and the policies are linked above.
        </p>
        <!-- The trial terms follow the trial button in the markup, so on phones they sit directly under it (legal B1.1);
             from 640 px the buttons share a row and the terms take their own line under it. -->
        <div class="tr-next__actions">
          <a
            :href="addReferralToUrl('https://app.workspace369.com/signup')"
            class="tr-next__primary"
            data-conversion-event="start_free_click"
            data-conversion-label="Trust page start free"
            data-conversion-location="/trust"
          >
            Start free
          </a>
          <div class="tr-next__terms"><TrialDisclosure /></div>
          <NuxtLink
            :to="localePath('/pricing')"
            class="tr-next__ghost"
            data-conversion-event="pricing_click"
            data-conversion-label="Trust page pricing"
            data-conversion-location="/trust"
          >
            See pricing
          </NuxtLink>
        </div>
      </section>
    </div>
    <PhoneStickyCta label="Trust" :location="$route.path" watch=".tr-hero" />
  </main>
</template>

<script setup>
import { localizedUrl } from "~/data/locales";
import { blogIllustrationForMotif } from "~/data/blog-illustrations";

const { addReferralToUrl } = useReferral();
const { canonicalLocaleCode, localePath } = useWorkspaceLocale();
const siteUrl = "https://workspace369.com";
const canonicalUrl = computed(() => localizedUrl(siteUrl, "/trust", canonicalLocaleCode.value));

const heroArt = blogIllustrationForMotif("security");
const portalArt = blogIllustrationForMotif("portal");
const reportingArt = blogIllustrationForMotif("reporting");

/* Every claim traces to existing copy: the previous trust page, the feature
   pages/FeaturesList copy, or the legal pages' own section headings. */
const securityPractices = [
  {
    label: "Payments",
    title: "Payments run through Stripe workflows.",
    copy:
      "Online payment links, ACH or bank transfer where configured, payment plans, refunds, and payment status tracking — payment processing is Stripe-powered, and the privacy policy documents what is shared.",
  },
  {
    label: "Access",
    title: "Access stays scoped to the client.",
    copy:
      "Contractors and account managers can be limited to assigned clients and the work that belongs to them.",
  },
  {
    label: "Accountability",
    title: "Automations are testable and logged.",
    copy:
      "Workflow automations can be simulated before anything sends, and every execution is logged.",
  },
  {
    label: "Revenue operations",
    title: "Retainers, recurring billing, and revenue reporting run deep.",
    copy:
      "Retainers, recurring invoices, payment plans, and split payments — with AR aging and project and customer profitability reporting on the higher tiers.",
  },
  {
    label: "Scale",
    title: "Enterprise is a published plan, not a custom quote.",
    copy:
      "$699/mo with 50 seats included, 30× Cadet usage, audit trails, and dedicated support.",
  },
  {
    label: "People",
    title: "Security questions reach a human.",
    copy:
      "Product, account, billing, or security concerns go to support@workspace369.com — buying questions to sales@workspace369.com.",
  },
];

const accountSecurity = [
  {
    label: "Sign-in",
    title: "Phone-based multi-factor authentication is on by default.",
    copy:
      "Every account starts with a code sent to your phone at sign-in. The account holder can turn it off in account security settings.",
  },
  {
    label: "Notes",
    title: "Notes can be password-protected and encrypted.",
    copy:
      "A protected note is encrypted with AES-256 using a key made from the password you set.",
  },
  {
    label: "Your data",
    title: "The workspace owner can export or delete the workspace.",
    copy:
      "The owner requests a full data export and gets a download link that works for 24 hours. Deleting a workspace runs after a 24-hour grace period, and the owner can cancel before it ends.",
  },
  {
    label: "Signatures",
    title: "Signing records are tamper-evident.",
    copy:
      "Each signing event on a contract is chained to the one before it with SHA-256 hashes, so a changed record shows.",
  },
  {
    label: "Inbox",
    title: "Inbound email is checked for phishing.",
    copy:
      "Messages get sender authentication checks (SPF, DKIM, and DMARC), a scam risk score, lookalike-domain and brand impersonation checks, and attachment malware scanning. Risky mail can be held in quarantine.",
  },
  {
    label: "Permissions",
    title: "Roles and permissions decide who sees what.",
    copy:
      "Use the admin, manager, member, and viewer roles, or set permissions screen by screen. Contractors can be limited to the clients assigned to them.",
  },
];

const legalLinks = [
  {
    title: "Privacy policy",
    path: "/privacy-policy",
    copy:
      "What Workspace369 collects, how it uses information — including AI features and Google API data — and how sharing, international transfers, retention, and security work.",
  },
  {
    title: "Terms of service",
    path: "/terms-of-service",
    copy:
      "The agreement for using the service: accounts, subscriptions, billing and trials, payments, customer data, communications compliance, and AI features.",
  },
  {
    title: "Cookie policy",
    path: "/cookie-policy",
    copy:
      "What cookies are, which kinds this site uses — strictly necessary, analytics, and marketing — and what each kind does.",
  },
];

const reliabilityLinks = [
  {
    title: "Changelog",
    path: "/changelog",
    copy:
      "Dated product updates across automations, inventory, proposals, AI, inbox, voice, requests, and reporting.",
  },
  {
    title: "Roadmap",
    path: "/roadmap",
    copy: "What is available, improving, and still being explored — direction, not promises.",
  },
  {
    title: "Support",
    path: "/support",
    copy:
      "Tell us where you are stuck — product, account, or billing questions reach the support inbox.",
  },
  {
    title: "Public pricing",
    path: "/pricing",
    copy:
      "Five plans with seat counts are published openly, and the same feature matrix ships as a public JSON feed the app reads.",
  },
];

const bestFit = [
  "A buyer wants fewer tabs across CRM, project delivery, inbox, invoicing, payments, files, AI, and reporting.",
  "The team sells client work, then needs to deliver, bill, follow up, and report without rebuilding context in another app.",
  "The same workspace runs solo operators through multi-team organizations — the Enterprise plan adds 50 seats, audit trails, and dedicated support.",
];

/* Specialist-tool tradeoffs only — never scale limits. Workspace369 covers
   advanced revenue operations and enterprise scale; these are the places where
   the platform genuinely does not go trade-deep. */
const betterElsewhere = [
  "A finance team needs dedicated tax, payroll, or accountant-of-record workflows.",
  "A security company needs guard-tour GPS verification, a retailer needs POS registers, a photography studio needs gallery delivery, or an MSP needs RMM agents.",
  "A team only needs a lightweight notes/wiki tool, phone system, or calendar planner rather than a connected operating workspace.",
];

const faq = [
  {
    q: "Is Workspace369 a CRM?",
    a:
      "Workspace369 includes CRM and client records, but it is broader than a CRM. It connects the client record to requests, projects, communication, billing, payments, files, AI, automations, accounting, inventory, and reporting.",
  },
  {
    q: "Is Workspace369 fit for larger organizations?",
    a:
      "Yes. The Enterprise plan is $699/mo with 50 seats included, 30× Cadet usage, audit trails, and dedicated support — and revenue operations run deep: retainers, recurring billing, payment plans, split payments, AR aging, and profitability reporting.",
  },
  {
    q: "Does Workspace369 replace every tool in a company?",
    a:
      "Not always. Workspace369 covers CRM, delivery, communication, billing, and reporting at enterprise depth, but trade-specific tools still belong to specialists: guard-tour GPS verification, POS registers, gallery delivery for photographers, RMM agents for MSPs, or dedicated tax and payroll platforms.",
  },
  {
    q: "How is payment information handled?",
    a:
      "Payments run through Stripe workflows: online payment links, ACH or bank transfer where configured, payment plans, refunds, and payment status tracking. The privacy policy's payments section describes what is shared with Stripe.",
  },
  {
    q: "How should a buyer verify current pricing and feature fit?",
    a:
      "Review the public pricing page, inspect the relevant feature pages, compare the vendor pages linked from each comparison page, and contact sales for edge cases such as large teams or custom compliance needs.",
  },
  {
    q: "Is two-factor authentication required?",
    a:
      "Phone-based multi-factor authentication is on by default for every account. The account holder can turn it off in account security settings.",
  },
  {
    q: "Where should security or account concerns be sent?",
    a:
      "Send product, account, billing, or security concerns to support@workspace369.com. Sales questions can go to sales@workspace369.com.",
  },
];

const entityFacts = [
  {
    label: "Product category",
    value: "Business application and client work operating system.",
  },
  {
    label: "Primary audience",
    value: "Agencies, consultants, operators, solo entrepreneurs, and client-work teams.",
  },
  {
    label: "Core workflows",
    value:
      "CRM, requests, projects, calendar, inbox, SMS, voice, voicemail, proposals, invoices, payments, files, AI, automations, accounting, inventory, and reports.",
  },
  {
    label: "Pricing range",
    value: "$29/mo to $699/mo across five public plans, with annual billing savings.",
  },
  {
    label: "App access",
    value: "Workspace369 is available on the web, with an iOS App Store listing.",
  },
  {
    label: "Public trust references",
    value: "Privacy policy, terms of service, help center, changelog, pricing, comparison pages, and contact-sales page.",
  },
];

useHead({
  title: "Workspace369 Trust Center | Company Facts and Policies",
  meta: [
    {
      name: "description",
      content:
        "How Workspace369 handles your data and keeps your business running: security practices, privacy and terms, reliability records, pricing and buyer answers.",
    },
    { name: "twitter:title", content: "Workspace369 Trust Center | Company Facts and Policies" },
    {
      name: "twitter:description",
      content:
        "How Workspace369 handles your data and keeps your business running: security practices, privacy and terms, reliability records, pricing and buyer answers.",
    },
    { name: "twitter:card", content: "summary_large_image" },
    { property: "og:title", content: "Workspace369 Trust Center | Company Facts and Policies" },
    {
      property: "og:description",
      content:
        "How Workspace369 handles your data and keeps your business running: security practices, privacy and terms, reliability records, pricing and buyer answers.",
    },
  ],
  script: [
    {
      key: "workspace369-trust-page",
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "ItemList",
            "@id": `${canonicalUrl.value}#entity-facts`,
            name: "Workspace369 entity facts",
            itemListElement: entityFacts.map((fact, index) => ({
              "@type": "ListItem",
              position: index + 1,
              name: fact.label,
              description: fact.value,
            })),
          },
          {
            "@type": "FAQPage",
            "@id": `${canonicalUrl.value}#faq`,
            mainEntity: faq.map((item) => ({
              "@type": "Question",
              name: item.q,
              acceptedAnswer: {
                "@type": "Answer",
                text: item.a,
              },
            })),
          },
        ],
      }),
    },
  ],
});
</script>

<style scoped>
.tr {
  background: #ffffff;
  color: #0f172a;
}

.tr-col {
  width: min(100% - 2rem, 72rem);
  margin: 0 auto;
}

/* ---------- Hero (editorial dark split) ---------- */
.tr-hero {
  background: #020617;
  padding: 6.5rem 0 3.5rem;
}

.tr-hero__shell {
  display: grid;
  gap: 2.5rem;
  align-items: center;
  width: min(100% - 2rem, 72rem);
  margin: 0 auto;
}

.tr-hero h1 {
  margin-top: 1.5rem;
  color: #ffffff;
  font-size: clamp(2.35rem, 5.4vw, 3.85rem);
  font-weight: 880;
  letter-spacing: 0;
  line-height: 1.05;
  text-wrap: balance;
}

.tr-lede {
  max-width: 38rem;
  margin-top: 1.25rem;
  color: #cbd5e1;
  font-size: clamp(1.02rem, 1.8vw, 1.18rem);
  line-height: 1.68;
}

.tr-hero__facts {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem 0;
  margin-top: 1.75rem;
  color: #94a3b8;
  font-size: 0.82rem;
  font-weight: 700;
  line-height: 1.5;
}

.tr-hero__facts span + span::before {
  margin: 0 0.65rem;
  color: #334155;
  content: "·";
}

.tr-hero__visual {
  overflow: hidden;
  margin: 0;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #ffffff;
  padding: 2.5rem;
}

.tr-hero__visual img {
  display: block;
  width: 100%;
  height: auto;
}

@media (min-width: 900px) {
  .tr-hero {
    padding: 10rem 0 5rem;
  }

  .tr-hero__shell {
    grid-template-columns: minmax(0, 1.2fr) minmax(0, 1fr);
    gap: clamp(2rem, 4vw, 3.5rem);
  }
}

@media (min-width: 1100px) {
  .tr-hero {
    padding-top: 11rem;
  }

  .tr-hero__shell {
    width: min(100% - 3rem, 72rem);
  }
}

/* ---------- Sections (changelog rhythm) ---------- */
.tr-section {
  border-top: 1px solid #e2e8f0;
  padding: 4rem 0;
}

.tr-section:first-of-type {
  border-top: 0;
  margin-top: 3.5rem;
}

.tr-head {
  max-width: 44rem;
}

.tr-head h2 {
  margin-top: 0.55rem;
  color: #020617;
  font-size: clamp(1.6rem, 3vw, 2.1rem);
  font-weight: 880;
  letter-spacing: 0;
  line-height: 1.15;
  text-wrap: balance;
}

.tr-split {
  display: grid;
  gap: 2.5rem;
  align-items: start;
  margin-top: 2.25rem;
}

@media (min-width: 960px) {
  .tr-split {
    grid-template-columns: minmax(0, 1fr) minmax(0, 24rem);
  }
}

/* Rows: hairline-separated, strong claim + one-line copy */
.tr-section > .tr-rows {
  margin-top: 2.25rem;
}

.tr-rows {
  display: grid;
}

.tr-row {
  display: grid;
  gap: 0.3rem;
  border-bottom: 1px solid #f1f5f9;
  padding: 1.1rem 0;
}

.tr-row:first-child {
  padding-top: 0;
}

.tr-row:last-child {
  border-bottom: 0;
}

.tr-row__label {
  color: #0e7490;
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0;
  text-transform: uppercase;
}

.tr-row h3 {
  color: #0f172a;
  font-size: 1.02rem;
  font-weight: 800;
  line-height: 1.35;
}

.tr-row p {
  color: #475569;
  font-size: 0.92rem;
  line-height: 1.65;
}

.tr-row--link {
  gap: 0.15rem;
}

.tr-row__title {
  color: #0f172a;
  font-size: 1rem;
  font-weight: 750;
  text-decoration: none;
}

.tr-row__title:hover {
  color: #1ba7ce;
}

/* Framed illustration figures, thin border + honest caption */
.tr-figure {
  margin: 0;
}

.tr-figure img {
  display: block;
  width: 100%;
  height: auto;
  border: 1px solid #dbe7f0;
  border-radius: 0.5rem;
  background: #ffffff;
}

.tr-figure figcaption {
  margin-top: 0.65rem;
  color: #64748b;
  font-size: 0.8rem;
  line-height: 1.5;
}

/* ---------- Fit pair ---------- */
.tr-fit-grid {
  display: grid;
  gap: 1rem;
  margin-top: 2.25rem;
}

.tr-fit-card {
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  background: #ffffff;
  padding: 1.4rem 1.5rem;
}

.tr-fit-card h3 {
  margin-top: 0.5rem;
  color: #0f172a;
  font-size: 1.08rem;
  font-weight: 800;
  line-height: 1.4;
}

.tr-fit-card ul {
  display: grid;
  gap: 0.6rem;
  margin-top: 0.9rem;
}

.tr-fit-card li {
  position: relative;
  padding-left: 1rem;
  color: #475569;
  font-size: 0.92rem;
  line-height: 1.6;
}

.tr-fit-card li::before {
  position: absolute;
  top: 0.6rem;
  left: 0;
  width: 0.34rem;
  height: 0.34rem;
  border-radius: 999px;
  background: #1ba7ce;
  content: "";
}

@media (min-width: 760px) {
  .tr-fit-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

/* ---------- FAQ (goals treatment, editorial tokens) ---------- */
.tr-faq__list {
  display: grid;
  gap: 0.7rem;
  margin-top: 2.25rem;
}

.tr-faq__item {
  overflow: hidden;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  background: #ffffff;
  transition: border-color 150ms ease;
}

.tr-faq__item[open] {
  border-color: rgb(14 116 144 / 0.4);
}

.tr-faq__item summary {
  display: flex;
  gap: 1rem;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  padding: 1.05rem 1.2rem;
  list-style: none;
}

.tr-faq__item summary::-webkit-details-marker {
  display: none;
}

.tr-faq__item summary h3 {
  color: #0f172a;
  font-size: 0.97rem;
  font-weight: 800;
  line-height: 1.35;
}

.tr-faq__item summary svg {
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
  color: #94a3b8;
  transition: transform 160ms ease, color 160ms ease;
}

.tr-faq__item[open] summary svg {
  color: #0e7490;
  transform: rotate(180deg);
}

.tr-faq__item > p {
  border-top: 1px solid #f1f5f9;
  padding: 1rem 1.2rem 1.2rem;
  color: #475569;
  font-size: 0.92rem;
  line-height: 1.7;
}

/* ---------- Close (typographic, editorial idiom) ---------- */
.tr-next {
  margin-top: 4rem;
  border-top: 1px solid #e2e8f0;
  padding: 4rem 0 5rem;
}

.tr-next h2 {
  max-width: 50rem;
  margin-top: 1rem;
  color: #020617;
  font-size: clamp(2.05rem, 5.4vw, 3.5rem);
  font-weight: 880;
  letter-spacing: 0;
  line-height: 1.06;
  text-wrap: balance;
}

.tr-next__copy {
  max-width: 42rem;
  margin-top: 1.25rem;
  color: #475569;
  font-size: 1.05rem;
  line-height: 1.7;
}

.tr-next__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 2.25rem;
}

/* Phones stack the buttons with the terms between them; from 640 px the terms move to their own line under the row.
   The wrapper stays uncapped so its full-width basis always breaks the row; the 34rem cap stays on the line. */
@media (max-width: 639px) {
  .tr-next__actions { flex-direction: column; align-items: flex-start; }
  .tr-next__terms :deep(.trial-disclosure) { margin-top: 0; }
}
@media (min-width: 640px) {
  .tr-next__terms { order: 1; flex-basis: 100%; margin-top: -0.2rem; }
}

.tr-next__primary,
.tr-next__ghost {
  display: inline-flex;
  min-height: 3rem;
  align-items: center;
  justify-content: center;
  border-radius: 0.5rem;
  padding: 0.7rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  text-decoration: none;
  transition: background-color 150ms ease, border-color 150ms ease, color 150ms ease;
}

.tr-next__primary {
  background: #0e7490;
  color: #ffffff;
}

.tr-next__primary:hover {
  background: #155e75;
}

.tr-next__ghost {
  border: 1px solid #cbd5e1;
  color: #0f172a;
}

.tr-next__ghost:hover {
  border-color: #94a3b8;
}

/* ---------- Dark ---------- */
.dark .tr {
  background: #020617;
  color: #e2e8f0;
}

.dark .tr-head h2,
.dark .tr-row h3,
.dark .tr-row__title,
.dark .tr-fit-card h3,
.dark .tr-faq__item summary h3,
.dark .tr-next h2 {
  color: #ffffff;
}

.dark .tr-row p,
.dark .tr-fit-card li,
.dark .tr-faq__item > p,
.dark .tr-next__copy {
  color: #cbd5e1;
}

.dark .tr-figure figcaption {
  color: #94a3b8;
}

.dark .tr-row__label {
  color: #67e8f9;
}

.dark .tr-row__title:hover,
.dark .tr-next__ghost:hover {
  color: #67e8f9;
}

.dark .tr-section,
.dark .tr-next {
  border-top-color: #1e293b;
}

.dark .tr-row {
  border-bottom-color: #16203a;
}

.dark .tr-faq__item > p {
  border-top: 1px solid #1e293b;
}

.dark .tr-figure img {
  border-color: #1e293b;
  background: #ffffff;
}

.dark .tr-fit-card {
  border-color: #1e293b;
  background: rgb(15 23 42 / 0.72);
}

.dark .tr-faq__item {
  border-color: #1e293b;
  background: rgb(15 23 42 / 0.72);
}

.dark .tr-faq__item[open] {
  border-color: rgb(103 232 249 / 0.3);
}

.dark .tr-faq__item[open] summary svg {
  color: #67e8f9;
}

.dark .tr-next__ghost {
  border-color: #334155;
  color: #e2e8f0;
}

@media (max-width: 640px) {
  .tr-hero {
    padding: 6.25rem 0 2.5rem;
  }

  .tr-section {
    padding: 3rem 0;
  }
}
</style>
